import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { vendorsApi, api, logActivity } from '../api/client';
import StageGuide from '../components/StageGuide';

function formatDollars(value: number | null | undefined): string {
  if (!value && value !== 0) return '—';
  return '$' + value.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

const ADMIN_EMAILS = ['jordan@firstoinvest.com'];

export default function Vendors() {
  // Admin gate for bulk vendor CSV upload. Reads the signed-in email cached by Layout.
  const isAdmin = ADMIN_EMAILS.includes((localStorage.getItem('fti_current_user') || '').toLowerCase());
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [dbCount, setDbCount] = useState<number>(0); // true total vendor count (from API)
  const [search, setSearch] = useState('');
  const searchTimerRef = useRef<any>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(500);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<any>(null);
  const [samKey, setSamKey] = useState('vendor-imports/SAM_PUBLIC_MONTHLY_V2_20260907.dat');
  const [samStatus, setSamStatus] = useState<any>(null);
  const [samStarting, setSamStarting] = useState(false);
  const [samErr, setSamErr] = useState('');
  const [sortCol, setSortCol] = useState<string>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);

  const handleSort = (col: string) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir(col === 'total_dollars' || col === 'total_awards_count' ? 'desc' : 'asc');
    }
  };

  const setFilter = (col: string, value: string) => {
    setFilters(f => {
      const next = { ...f };
      if (value) next[col] = value;
      else delete next[col];
      return next;
    });
  };

  // Get unique values for filter dropdowns
  const filterOptions = React.useMemo(() => {
    const opts: Record<string, string[]> = { sector: [], department: [], subtier: [], office: [] };
    const sets: Record<string, Set<string>> = { sector: new Set(), department: new Set(), subtier: new Set(), office: new Set() };
    items.forEach(v => {
      if (v.sector) sets.sector.add(v.sector);
      if (v.department) sets.department.add(v.department);
      if (v.subtier) sets.subtier.add(v.subtier);
      if (v.office) sets.office.add(v.office);
    });
    opts.sector = Array.from(sets.sector).sort();
    opts.department = Array.from(sets.department).sort();
    opts.subtier = Array.from(sets.subtier).sort();
    opts.office = Array.from(sets.office).sort();
    return opts;
  }, [items]);

  const sortedItems = React.useMemo(() => {
    // First filter (client-side for non-name columns; name filter triggers server search)
    let filtered = items;
    if (Object.keys(filters).length > 0) {
      filtered = items.filter(v => {
        return Object.entries(filters).every(([col, val]) => {
          if (col === 'name' && search) return true; // Name filter handled by server search
          const itemVal = (v[col] || '').toLowerCase();
          return itemVal.includes(val.toLowerCase());
        });
      });
    }
    // Then sort
    const sorted = [...filtered].sort((a, b) => {
      let aVal = a[sortCol] ?? '';
      let bVal = b[sortCol] ?? '';
      if (sortCol === 'total_dollars' || sortCol === 'total_awards_count') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [items, sortCol, sortDir, filters, search]);

  const load = useCallback(async () => {
    if (!search) { setItems([]); setTotal(dbCount); return; }  // Don't load without search
    setLoading(true);
    const params: Record<string, string> = { page_size: String(pageSize), page: String(page) };
    if (search) params.search = search;
    const res = await vendorsApi.list(params).catch(() => ({ items: [], total: 0 }));
    setItems(res.items || []);
    setTotal(res.total || 0);
    setLoading(false);
  }, [search, page, pageSize, dbCount]);

  useEffect(() => { load(); }, [load]);

  // Fetch the true vendor count once on mount (cached server-side).
  useEffect(() => {
    api.get<any>('/vendors/count').then(c => setDbCount(c.total || 0)).catch(() => {});
  }, []);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // The file is split into ~3.5 MB chunks below, so only browser memory limits the
    // total size, not the Lambda request. Allow large SAM.gov / Carahsoft exports.
    if (file.size > 300 * 1024 * 1024) {
      setUploadResult({ error: 'File too large for the browser to process (over 300 MB). Split it into smaller files.' });
      if (csvInputRef.current) csvInputRef.current.value = '';
      return;
    }

    setUploading(true);
    setUploadResult(null);
    setUploadProgress('Reading file...');

    try {
      // Read file as base64 and send in size-based chunks
      const text = await file.text();
      const CHUNK_BYTE_LIMIT = 3_500_000; // ~3.5 MB per request (safe under 6 MB Lambda limit after JSON wrapping)

      // CSV-aware row splitting that respects quoted fields
      const rows: string[] = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (ch === '"') {
          inQuotes = !inQuotes;
          current += ch;
        } else if ((ch === '\n' || ch === '\r') && !inQuotes) {
          if (ch === '\r' && text[i + 1] === '\n') i++;
          if (current.trim().length > 0) rows.push(current);
          current = '';
        } else {
          current += ch;
        }
      }
      if (current.trim().length > 0) rows.push(current);

      const header = rows[0];
      const dataRows = rows.slice(1);

      // Split into chunks that stay under the byte limit
      const chunks: string[][] = [];
      let currentChunk: string[] = [];
      let currentSize = header.length + 1;

      for (const row of dataRows) {
        const rowSize = row.length + 1;
        if (currentSize + rowSize > CHUNK_BYTE_LIMIT && currentChunk.length > 0) {
          chunks.push(currentChunk);
          currentChunk = [];
          currentSize = header.length + 1;
        }
        currentChunk.push(row);
        currentSize += rowSize;
      }
      if (currentChunk.length > 0) chunks.push(currentChunk);

      const totalChunks = chunks.length;
      let totalParsed = 0;
      let totalUpserted = 0;
      let allErrors: string[] = [];

      for (let i = 0; i < totalChunks; i++) {
        setUploadProgress(`Uploading batch ${i + 1} of ${totalChunks}...`);
        const csvChunk = [header, ...chunks[i]].join('\n');
        const base64Chunk = btoa(unescape(encodeURIComponent(csvChunk)));

        try {
          const result = await vendorsApi.uploadCsvChunk(base64Chunk, i, totalChunks);
          totalParsed += result.rows_parsed || 0;
          totalUpserted += result.rows_upserted || result.rows_updated || 0;
          if (result.errors?.length) {
            allErrors = allErrors.concat(result.errors.slice(0, 10));
          }
        } catch (err: any) {
          allErrors.push(`Batch ${i + 1} failed: ${err.message}`);
        }
      }

      setUploadResult({
        rows_parsed: totalParsed,
        rows_upserted: totalUpserted,
        errors: allErrors.length > 0 ? allErrors : undefined,
      });
      setPage(1);
      load();
    } catch (err: any) {
      setUploadResult({ error: err.message });
    }
    setUploading(false);
    setUploadProgress('');
    // Reset input
    if (csvInputRef.current) csvInputRef.current.value = '';
  };

  const handleSyncKB = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await vendorsApi.ingestKnowledgeBase();
      setSyncResult(result);
    } catch (err: any) {
      setSyncResult({ error: err.message });
    }
    setSyncing(false);
  };

  // ── SAM monthly import (admin) ──
  const startSamImport = async () => {
    setSamErr('');
    if (!samKey.trim()) { setSamErr('Enter the S3 key of the uploaded .dat file.'); return; }
    setSamStarting(true);
    try {
      await vendorsApi.importSam(samKey.trim());
      const s = await vendorsApi.importSamStatus();
      setSamStatus(s);
    } catch (err: any) {
      setSamErr(err?.message || 'Could not start the import.');
    }
    setSamStarting(false);
  };

  // Poll import status (loads current state on mount for admins; polls while running).
  useEffect(() => {
    if (!isAdmin) return;
    let timer: any;
    const tick = async () => {
      try {
        const s = await vendorsApi.importSamStatus();
        setSamStatus(s);
        if (s?.status === 'running') timer = setTimeout(tick, 5000);
      } catch { /* ignore */ }
    };
    tick();
    return () => { if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const totalPages = Math.ceil(total / pageSize);

  const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 600, color: '#374151', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: '#f8fafc', borderBottom: '2px solid #e2e8f0', cursor: 'pointer', userSelect: 'none', letterSpacing: '0.02em' };
  const td: React.CSSProperties = { padding: '6px 10px', fontSize: 11, borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' };
  const tdWrap: React.CSSProperties = { padding: '6px 10px', fontSize: 11, borderBottom: '1px solid #f1f5f9', verticalAlign: 'top', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.4' };
  const tdNoWrap: React.CSSProperties = { padding: '6px 10px', fontSize: 11, borderBottom: '1px solid #f1f5f9', verticalAlign: 'top', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  const thFilter: React.CSSProperties = { padding: '4px 6px', position: 'sticky', top: 32, background: '#f8fafc', borderBottom: '1px solid #e2e8f0' };
  const filterInput: React.CSSProperties = { width: '100%', padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 10, boxSizing: 'border-box', color: '#374151', background: 'white' };
  const filterSelect: React.CSSProperties = { width: '100%', padding: '3px 4px', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 10, boxSizing: 'border-box', color: '#374151', background: 'white' };

  const SortIcon = ({ col }: { col: string }) => {
    if (sortCol !== col) return <span style={{ color: '#d1d5db', marginLeft: 2 }}>↕</span>;
    return <span style={{ color: '#2563eb', marginLeft: 2 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <div>
      <StageGuide id="vendors" />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22 }}>Vendor Database</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>{dbCount ? dbCount.toLocaleString() : '—'} registered vendors from SAM.gov. Use filters to find vendors by capability.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {isAdmin && (
            <>
              <input ref={csvInputRef} type="file" accept=".csv,.dat,text/csv,text/plain" onChange={handleUpload} style={{ display: 'none' }} id="vendor-csv-upload" />
              <label htmlFor="vendor-csv-upload" title="Bulk-upload vendors from a CSV or SAM.gov .dat extract (comma, pipe, or tab delimited). Admin only."
                style={{ padding: '8px 16px', background: uploading ? '#94a3b8' : '#0f766e', color: 'white', borderRadius: 6, fontSize: 12, cursor: uploading ? 'default' : 'pointer', fontWeight: 500 }}>
                {uploading ? (uploadProgress || 'Uploading…') : '⬆ Upload Vendors CSV / .dat'}
              </label>
            </>
          )}
          <input ref={fileInputRef} type="file" accept=".pdf" onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            setUploading(true);
            setUploadResult(null);
            const reader = new FileReader();
            reader.onload = async () => {
              const base64 = (reader.result as string).split(',')[1];
              try {
                const result = await api.post<any>('/vendors/upload-card', { file_content: base64, filename: file.name });
                setUploadResult({ message: result.message, vendor: result.extracted_data });
                load();
              } catch (err: any) {
                setUploadResult({ error: err.message });
              }
              setUploading(false);
            };
            reader.readAsDataURL(file);
          }} style={{ display: 'none' }} id="vendor-card-upload" />
          <label htmlFor="vendor-card-upload" style={{ padding: '8px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 500, opacity: uploading ? 0.6 : 1 }}>
            {uploading ? 'Processing...' : 'Upload Vendor Card (PDF)'}
          </label>
        </div>
      </div>

      {/* Upload result banner */}
      {uploadResult && (
        <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 6, fontSize: 13, background: uploadResult.error ? '#fef2f2' : '#f0fdf4', border: `1px solid ${uploadResult.error ? '#fecaca' : '#bbf7d0'}`, color: uploadResult.error ? '#991b1b' : '#166534' }}>
          {uploadResult.error ? `Upload failed: ${uploadResult.error}` : `Uploaded ${uploadResult.rows_parsed} vendors (${uploadResult.rows_upserted} upserted)${uploadResult.errors?.length ? ` — ${uploadResult.errors.length} errors` : ''}`}
          <button onClick={() => setUploadResult(null)} style={{ marginLeft: 12, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, textDecoration: 'underline', color: 'inherit' }}>dismiss</button>
        </div>
      )}

      {/* Admin: SAM monthly (.dat) import from S3 — for files too large for browser upload */}
      {isAdmin && (
        <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e5e7eb', padding: 16, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Import SAM monthly (.dat) from S3</span>
            <span style={{ fontSize: 11, color: '#64748b' }}>for large full-registry files (500 MB+)</span>
          </div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10, lineHeight: 1.5 }}>
            First upload the file to the docs bucket once, e.g.<br />
            <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 4 }}>aws s3 cp "SAM_PUBLIC_MONTHLY_V2_20260907.dat" s3://fti-simple-docs-424572540514/vendor-imports/</code><br />
            then enter the key below and start. It runs in the background and upserts by UEI (updates existing, adds new).
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input value={samKey} onChange={e => setSamKey(e.target.value)}
              placeholder="vendor-imports/SAM_PUBLIC_MONTHLY_V2_YYYYMMDD.dat"
              style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12 }} />
            <button onClick={startSamImport} disabled={samStarting || samStatus?.status === 'running'}
              style={{ padding: '8px 16px', background: (samStarting || samStatus?.status === 'running') ? '#94a3b8' : '#0f766e', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: (samStarting || samStatus?.status === 'running') ? 'default' : 'pointer' }}>
              {samStatus?.status === 'running' ? 'Import running…' : samStarting ? 'Starting…' : 'Start import'}
            </button>
          </div>
          {samErr && <div style={{ marginTop: 8, fontSize: 12, color: '#991b1b' }}>{samErr}</div>}
          {samStatus && samStatus.status !== 'idle' && (
            <div style={{ marginTop: 10, fontSize: 12, color: '#334155' }}>
              {(() => {
                const total = Number(samStatus.bytes_total || 0);
                const done = Number(samStatus.bytes_done || 0);
                const pct = total ? Math.min(100, Math.round((done / total) * 100)) : 0;
                const color = samStatus.status === 'complete' ? '#166534' : samStatus.status === 'error' ? '#991b1b' : '#0f766e';
                return (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontWeight: 600, color, textTransform: 'capitalize' }}>{samStatus.status}</span>
                      <span>{pct}% · {Number(samStatus.rows_upserted || 0).toLocaleString()} new · {Number(samStatus.rows_updated || 0).toLocaleString()} updated</span>
                    </div>
                    <div style={{ height: 8, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: color, transition: 'width .4s' }} />
                    </div>
                    {samStatus.error && <div style={{ color: '#991b1b', marginTop: 6 }}>{samStatus.error}</div>}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Smart Filter Panel */}
      <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e5e7eb', padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>Search & Filter</div>
          <div style={{ background: '#eff6ff', padding: '3px 10px', borderRadius: 12, fontSize: 11, color: '#1d4ed8', fontWeight: 600 }}>{dbCount ? dbCount.toLocaleString() : '—'} vendors</div>
          {search && <div style={{ background: '#dcfce7', padding: '3px 10px', borderRadius: 12, fontSize: 11, color: '#166534', fontWeight: 500 }}>{total.toLocaleString()} results</div>}
        </div>
        
        {/* Main search */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <input
            style={{ flex: 1, padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}
            placeholder="Type vendor name (e.g. Deloitte, Palantir, Carahsoft)..."
            value={search}
            onChange={e => { setSearch(e.target.value); if (searchTimerRef.current) clearTimeout(searchTimerRef.current); searchTimerRef.current = setTimeout(() => { if (e.target.value.trim().length >= 3) { setPage(1); load(); } else if (e.target.value.trim().length === 0) { setPage(1); load(); } }, 600); }}
            onKeyDown={e => { if (e.key === 'Enter') { setPage(1); if (search.trim()) logActivity('search', { query: search.trim(), page: 'Vendors' }); load(); } }}
            aria-label="Search vendors"
          />
          <button onClick={() => { setPage(1); if (search.trim()) logActivity('search', { query: search.trim(), page: 'Vendors' }); load(); }} style={{ padding: '10px 20px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>Search</button>
          {search && <button onClick={() => { setSearch(''); setPage(1); setTimeout(() => load(), 100); }} style={{ padding: '10px 14px', background: '#f3f4f6', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>Clear</button>}
        </div>

        {/* Advanced filters grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
          <div>
            <label style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 3 }}>NAICS Code</label>
            <input value={filters.primary_naics || ''} onChange={e => setFilter('primary_naics', e.target.value)} placeholder="e.g. 541511" style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 3 }}>PSC Code</label>
            <input value={filters.psc_codes || ''} onChange={e => setFilter('psc_codes', e.target.value)} placeholder="e.g. D318, R425" style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 3 }}>Country</label>
            <input value={filters.country || ''} onChange={e => setFilter('country', e.target.value)} placeholder="e.g. USA, ISR" style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 3 }}>State</label>
            <input value={filters.state || ''} onChange={e => setFilter('state', e.target.value)} placeholder="e.g. VA, CA" style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 3 }}>CAGE Code</label>
            <input value={filters.cage_code || ''} onChange={e => setFilter('cage_code', e.target.value)} placeholder="e.g. 4VRA5" style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 3 }}>Socio-Economic</label>
            <input value={filters.socio_economic_type || ''} onChange={e => setFilter('socio_economic_type', e.target.value)} placeholder="e.g. SDVOSB, HUBZone" style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 3 }}>Past Award (SAM.gov)</label>
            <select value={filters.past_awards || ''} onChange={e => setFilter('past_awards', e.target.value)} style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, boxSizing: 'border-box', background: 'white', color: '#374151' }}>
              <option value="">Any</option>
              <option value="yes">Has past award</option>
              <option value="not">No past award</option>
            </select>
          </div>
        </div>
        
        {Object.keys(filters).length > 0 && (
          <div style={{ marginTop: 10, display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: '#6b7280' }}>Active filters:</span>
            {Object.entries(filters).filter(([,v]) => v).map(([k, v]) => (
              <span key={k} style={{ padding: '2px 8px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, fontSize: 10, color: '#1d4ed8' }}>{k}: {v} <span onClick={() => setFilter(k, '')} style={{ cursor: 'pointer', marginLeft: 4 }}>×</span></span>
            ))}
            <button onClick={() => setFilters({})} style={{ padding: '2px 8px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 12, fontSize: 10, cursor: 'pointer' }}>Clear All</button>
          </div>
        )}
      </div>

      {loading && <div style={{ color: '#6b7280', marginBottom: 12, fontSize: 13, textAlign: 'center' }}>Searching...</div>}

      {/* Results */}
      {!search && sortedItems.length === 0 && !loading ? (
        <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af', background: 'white', borderRadius: 10, border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🏢</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#374151', marginBottom: 6 }}>{dbCount ? dbCount.toLocaleString() : ''} Vendors Ready to Search</div>
          <div style={{ fontSize: 13, maxWidth: 500, margin: '0 auto', lineHeight: 1.6 }}>
            This database contains the full SAM.gov entity registry. Type a vendor name above or use the filters to find vendors by NAICS code, PSC code, location, or socio-economic status.
          </div>
        </div>
      ) : sortedItems.length > 0 && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 12 }}>
            {sortedItems.map((v: any) => (
              <Link key={v.vendor_id} to={`/vendors/${v.vendor_id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                <div style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 16, transition: 'all 0.15s', cursor: 'pointer', height: '100%' }} onMouseEnter={e => { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(37,99,235,0.1)'; }} onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', lineHeight: 1.3 }}>{v.name || '—'}</div>
                      <div style={{ display: 'flex', gap: 12, marginTop: 4, fontSize: 11, color: '#6b7280' }}>
                        <span style={{ fontFamily: 'monospace' }}>{v.uei || ''}</span>
                        {v.cage_code && <span>CAGE: <strong>{v.cage_code}</strong></span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      {v.past_awards === 'yes' && <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600 }}>🏆 Past Award</span>}
                      {v.is_small_business && <span style={{ background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600 }}>SB</span>}
                      {v.country && v.country !== 'USA' && <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600 }}>{v.country}</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 16, fontSize: 11, color: '#64748b', marginBottom: 6, flexWrap: 'wrap' }}>
                    {v.primary_naics && <span>NAICS: <strong style={{ color: '#1e40af' }}>{v.primary_naics}</strong></span>}
                    {Array.isArray(v.psc_codes) && v.psc_codes.length > 0 && <span>PSC: <strong style={{ color: '#047857' }}>{v.psc_codes.slice(0, 3).join(', ')}</strong></span>}
                    {v.state && <span>{v.state}{v.country === 'USA' ? '' : `, ${v.country}`}</span>}
                  </div>
                  {v.socio_economic_type && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
                      {v.socio_economic_type.split(',').filter(Boolean).map((s: string, i: number) => (
                        <span key={i} style={{ padding: '1px 6px', borderRadius: 8, fontSize: 9, background: '#f3e8ff', color: '#7c3aed', fontWeight: 500 }}>{s.trim()}</span>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, paddingTop: 8, borderTop: '1px solid #f3f4f6' }}>
                    {v.website ? <span style={{ fontSize: 10, color: '#2563eb' }}>{v.website.replace(/^https?:\/\//, '').substring(0, 30)}</span> : <span />}
                    <span style={{ fontSize: 10, color: '#2563eb', fontWeight: 500 }}>View Profile →</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', fontSize: 13, color: '#6b7280', marginTop: 16 }}>
              <span>Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, total)} of {total.toLocaleString()} results</span>
              <div style={{ display: 'flex', gap: 4 }}>
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={{ padding: '6px 14px', border: '1px solid #d1d5db', borderRadius: 6, background: 'white', cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.5 : 1, fontSize: 12 }}>← Prev</button>
                <span style={{ padding: '6px 14px', fontSize: 12 }}>Page {page} / {totalPages}</span>
                <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} style={{ padding: '6px 14px', border: '1px solid #d1d5db', borderRadius: 6, background: 'white', cursor: page >= totalPages ? 'not-allowed' : 'pointer', opacity: page >= totalPages ? 0.5 : 1, fontSize: 12 }}>Next →</button>
              </div>
            </div>
          )}
        </>
      )}

      {search && sortedItems.length === 0 && !loading && (
        <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', background: 'white', borderRadius: 8, border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: 14, fontWeight: 500 }}>No vendors found for "{search}"</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>Try a different search term or check spelling</div>
        </div>
      )}
    </div>
  );
}
